// Client-side PDF generator for an individual Strategy Profile submission.
// Uses jsPDF text APIs directly so output is searchable, selectable, and crisp.
// Visual mapping to brand:
//   • Headings  →  jsPDF built-in `times` (serif, close to Cormorant Garamond)
//   • Body      →  jsPDF built-in `helvetica` (close to Inter)
//   • Charcoal #1C1A17 header bar, Cream #F5F0E8 text, Gold #B89A6E accents

import { jsPDF } from "jspdf";

// Mirrors STRATEGY_SECTIONS from AdminPage.jsx (kept intentionally local so we can
// tweak the print presentation without coupling to admin UI state).
const SECTIONS = [
  { title: "01 · About You", fields: [
    ["first_name","First name"],["last_name","Last name"],["email","Email"],["phone","Phone"],
    ["dob","Date of birth"],["state","State of residence"],
    ["marital_status","Marital status"],["decision_maker","Decision maker"],
    ["employment","Employment"],["employment_other","Employment (other)"],
    ["industry","Industry"],["household","Household structure"],
    ["children","Children?"],["grandchildren","Grandchildren?"],
    ["dependent_ages","Dependent ages"],["housing","Housing"],["other_real_estate","Other real estate"],
  ]},
  { title: "02 · Family, Vision & Values", fields: [
    ["family_context","Family context"],["money_goal","Money goals"],
    ["rich_life","Rich life"],["financial_upbringing","Financial upbringing"],
    ["legacy_intent","Legacy intent"],["generational_hopes","Generational hopes"],
    ["motivation","Motivation"],["long_term_role","Long-term role"],
  ]},
  { title: "03 · Income & Cash Flow", fields: [
    ["income_sources","Income sources"],["primary_income","Primary annual income"],
    ["additional_income","Additional income"],["income_consistency","Income consistency"],
    ["retirement_contribution","Retirement contribution"],["outside_savings","Outside savings"],
    ["tax_filing","Tax filing"],["cpa","CPA"],["income_outlook","Income outlook"],
  ]},
  { title: "04 · Cash Flow & Money Habits", fields: [
    ["monthly_takehome","Monthly take-home"],["monthly_spend","Monthly spend"],
    ["money_flow_first","Money flow first"],["money_control_feeling","Money control feeling"],
    ["month_end","End of month"],["cashflow_clarity","Cashflow clarity"],
    ["budgeting","Budgeting"],["redirect_percent","Redirect %"],
    ["game_changing_habit","Game-changing habit"],["future_expenses","Future expenses"],
  ]},
  { title: "05 · Assets & Net Worth", fields: [
    ["assets","Assets"],["liquid_savings","Liquid savings"],
    ["emergency_fund","Emergency fund"],["retirement_total","Retirement total"],
  ]},
  { title: "06 · Liabilities & Debt", fields: [
    ["liabilities","Liabilities"],["total_debt","Total debt"],["money_mindset","Money mindset"],
  ]},
  { title: "07 · Goals & Priorities", fields: [
    ["top_12mo_goal","Top 12-mo goal"],["sixmo_milestone","6-mo milestone"],
    ["three_year_vision","3-year vision"],["goals","Goals"],
    ["life_events","Life events"],["biggest_obstacle","Biggest obstacle"],
    ["ibc_response","IBC reaction"],
  ]},
  { title: "08 · Protection & Insurance", fields: [
    ["insurance","Insurance"],["hlv_insured","Human life value insured"],
    ["child_insurance","Child insurance"],["income_gap_coverage","Income gap coverage"],
    ["ltc_plan","Long-term care plan"],["risk_tolerance","Risk tolerance"],
    ["banking_frustration","Banking frustration"],
  ]},
  { title: "09 · Health & Lifestyle", fields: [
    ["height","Height"],["weight","Weight"],
    ["tobacco","Tobacco"],["vape","Vape"],["marijuana","Marijuana"],
    ["prescriptions","Prescriptions"],["prescriptions_detail","Prescriptions detail"],
    ["pending_exams","Pending exams"],["pending_exams_detail","Pending exams detail"],
    ["pending_results","Pending results"],["pending_results_detail","Pending results detail"],
    ["insurability","Insurability concern"],["insurability_detail","Insurability detail"],
  ]},
  { title: "10 · Business, Estate & Legal", fields: [
    ["business_owner","Business owner"],["exit_plan","Exit plan"],
    ["buy_sell","Buy-sell / key person"],["will_trust","Will / trust"],
    ["beneficiaries","Beneficiaries"],
  ]},
  { title: "11 · Mindset & Final Thoughts", fields: [
    ["financial_literacy","Financial literacy (1-5)"],["concepts","Familiar concepts"],
    ["prior_advisor","Prior advisor"],["bad_experience","Bad experience"],
    ["commitment","Commitment (1-5)"],["trigger_moment","Trigger moment"],
    ["most_important","Most important"],["call_success","Call success"],
    ["referral_source","Referral source"],["anything_else","Anything else"],
  ]},
];

// Brand palette
const COLOR = {
  charcoal: [28, 26, 23],   // #1C1A17
  cream: [245, 240, 232],   // #F5F0E8
  gold: [184, 154, 110],    // #B89A6E
  goldLight: [217, 196, 160], // #D9C4A0
  body: [42, 38, 33],       // near-black body text on cream
  muted: [140, 132, 120],   // #8C8478
  hairline: [217, 196, 160],// gold hairline
};

const formatValue = (v) => {
  if (v === undefined || v === null || v === "") return "—";
  if (Array.isArray(v)) {
    if (v.length === 0) return "—";
    return v.map(String).join(" · ");
  }
  return String(v);
};

const formatSubmittedDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "long", day: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
};

const slugifyName = (s) =>
  (s || "submission").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "submission";

// jsPDF's built-in fonts use Latin-1. Map common Unicode glyphs that prospects
// might paste/type into safe equivalents so the PDF never shows tofu/garbage.
const UNICODE_MAP = {
  "\u2192": "->",   // →
  "\u2190": "<-",   // ←
  "\u2194": "<->",  // ↔
  "\u21D2": "=>",   // ⇒
  "\u2022": "•",    // already in Latin-1 but normalize
  "\u2026": "...",  // …
  "\u00A0": " ",    // nbsp
  "\u200B": "",     // zero-width space
  "\u200C": "",
  "\u200D": "",
  "\uFEFF": "",
};
const sanitize = (str) => {
  let out = String(str);
  for (const [k, v] of Object.entries(UNICODE_MAP)) {
    out = out.split(k).join(v);
  }
  // Strip any remaining non-Latin-1 chars to keep PDF clean
  return out.replace(/[^\x00-\xFF]/g, "");
};

export function generateStrategyProfilePdf(row) {
  const doc = new jsPDF({ unit: "pt", format: "letter" }); // 612 x 792 pt
  const PAGE_W = doc.internal.pageSize.getWidth();
  const PAGE_H = doc.internal.pageSize.getHeight();

  const MARGIN_X = 56;
  const HEADER_H = 76;        // charcoal bar height (first page only)
  const FOOTER_H = 36;
  const CONTENT_TOP = HEADER_H + 36;
  const CONTENT_BOTTOM = PAGE_H - FOOTER_H - 24;
  const LABEL_W = 150;        // left column for labels in field rows
  const GUTTER = 14;

  const fullName = [row.first_name, row.last_name].filter(Boolean).join(" ") || row.first_name || "—";
  const submittedDate = formatSubmittedDate(row.created_at);
  let pageNum = 1;

  // ── Helpers ──────────────────────────────────────────────────────────
  const drawHeaderBar = (firstPage = false) => {
    doc.setFillColor(...COLOR.charcoal);
    doc.rect(0, 0, PAGE_W, HEADER_H, "F");

    // Left text: "Liz DiRocco · Strategy Profile"
    doc.setTextColor(...COLOR.cream);
    doc.setFont("times", "italic");
    doc.setFontSize(16);
    doc.text("Liz DiRocco", MARGIN_X, HEADER_H / 2 - 2);

    // Subtitle eyebrow under the wordmark
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...COLOR.gold);
    doc.text("STRATEGY  PROFILE  ·  CONFIDENTIAL", MARGIN_X, HEADER_H / 2 + 14, { charSpace: 1.6 });

    // Right text: submission date
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(...COLOR.goldLight);
    const dateLabel = "SUBMITTED";
    doc.text(dateLabel, PAGE_W - MARGIN_X, HEADER_H / 2 - 4, { align: "right", charSpace: 1.6 });
    doc.setTextColor(...COLOR.cream);
    doc.setFontSize(11);
    doc.text(submittedDate, PAGE_W - MARGIN_X, HEADER_H / 2 + 12, { align: "right" });

    // Hairline under header
    doc.setDrawColor(...COLOR.gold);
    doc.setLineWidth(0.4);
    doc.line(0, HEADER_H, PAGE_W, HEADER_H);

    if (firstPage) {
      // Submitter name as large heading
      const nameTop = HEADER_H + 50;
      doc.setFont("times", "normal");
      doc.setFontSize(34);
      doc.setTextColor(...COLOR.body);
      doc.text(sanitize(fullName), MARGIN_X, nameTop);

      // Subline: email · phone · state
      const subParts = [row.email, row.phone, row.state].filter(Boolean).join("   ·   ");
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9.5);
      doc.setTextColor(...COLOR.muted);
      if (subParts) doc.text(sanitize(subParts), MARGIN_X, nameTop + 18);

      // Gold rule
      doc.setDrawColor(...COLOR.gold);
      doc.setLineWidth(0.8);
      doc.line(MARGIN_X, nameTop + 34, MARGIN_X + 44, nameTop + 34);
    }
  };

  const drawFooter = () => {
    doc.setDrawColor(...COLOR.hairline);
    doc.setLineWidth(0.3);
    doc.line(MARGIN_X, PAGE_H - FOOTER_H, PAGE_W - MARGIN_X, PAGE_H - FOOTER_H);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...COLOR.muted);
    doc.text(
      "Liz DiRocco  ·  Strategic Banking. Infinite Possibilities.",
      MARGIN_X, PAGE_H - FOOTER_H + 18,
      { charSpace: 0.4 }
    );
    doc.setTextColor(...COLOR.gold);
    doc.text(
      `Page ${pageNum}`,
      PAGE_W - MARGIN_X, PAGE_H - FOOTER_H + 18,
      { align: "right" }
    );
  };

  const newPage = () => {
    drawFooter();
    doc.addPage();
    pageNum += 1;
    drawHeaderBar(false);
  };

  // y cursor lives in pt from top of page
  let y = 0;

  const ensureSpace = (need) => {
    if (y + need > CONTENT_BOTTOM) {
      newPage();
      y = CONTENT_TOP;
    }
  };

  // Section heading in gold serif
  const drawSectionTitle = (title) => {
    ensureSpace(46);
    // Add breathing room before each new section
    y += 8;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(...COLOR.gold);
    doc.text(title.toUpperCase(), MARGIN_X, y, { charSpace: 2.2 });
    y += 8;
    // Gold hairline
    doc.setDrawColor(...COLOR.gold);
    doc.setLineWidth(0.5);
    doc.line(MARGIN_X, y, PAGE_W - MARGIN_X, y);
    y += 18;
  };

  // Field row: label (left) | value (right, wraps)
  const drawField = (label, value) => {
    const valueText = formatValue(value);
    if (valueText === "—") return false; // skip empties — we collapse them like the admin drawer

    const valueX = MARGIN_X + LABEL_W + GUTTER;
    const valueMaxW = PAGE_W - MARGIN_X - valueX;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    const wrapped = doc.splitTextToSize(sanitize(valueText), valueMaxW);
    const lineH = 14;
    const blockH = wrapped.length * lineH;

    ensureSpace(blockH + 8);

    // Label (uppercase, muted)
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.8);
    doc.setTextColor(...COLOR.muted);
    doc.text(label.toUpperCase(), MARGIN_X, y + 1, { charSpace: 1.8 });

    // Value (body color)
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(...COLOR.body);
    doc.text(wrapped, valueX, y + 1);

    y += blockH + 10;
    return true;
  };

  // ── Page 1 ───────────────────────────────────────────────────────────
  drawHeaderBar(true);
  y = CONTENT_TOP + 56; // leave room below the big name heading + rule

  // ── Sections ─────────────────────────────────────────────────────────
  SECTIONS.forEach((section) => {
    // Only print the section if at least one field has a value
    const hasAny = section.fields.some(([key]) => {
      const v = row[key];
      return !(v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0));
    });
    if (!hasAny) return;

    drawSectionTitle(section.title);
    section.fields.forEach(([key, label]) => drawField(label, row[key]));
  });

  // Closing line on the last page
  ensureSpace(40);
  y += 16;
  doc.setDrawColor(...COLOR.gold);
  doc.setLineWidth(0.6);
  doc.line(MARGIN_X, y, MARGIN_X + 36, y);
  y += 20;
  doc.setFont("times", "italic");
  doc.setFontSize(11);
  doc.setTextColor(...COLOR.muted);
  doc.text("End of Strategy Profile.", MARGIN_X, y);

  drawFooter();

  // Trigger download
  const safeName = slugifyName(fullName);
  const dateStamp = (row.created_at || new Date().toISOString()).slice(0, 10);
  doc.save(`strategy-profile_${safeName}_${dateStamp}.pdf`);
}
